import React, { useEffect, useState } from "react";
import axios from "axios";
import toast from "react-hot-toast";
import {
  Accordion,
  Badge,
  Button,
  Card,
  Group,
  Loader,
  Stack,
  Text,
} from "@mantine/core";
import { useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";

import { ErrorView, PageTitle } from "@components/shared";
import AssessmentViewerLinksPanel from "@components/Organization/AssessmentViewerLinksPanel";
import {
  AssessmentActivityTimeline,
  AssessmentOpccResults,
  AssessmentResponseLookup,
  AssessmentStenResults,
} from "@components/Assessment";
import entityIcon from "@util/entityIcon";
import entityColor from "@util/entityColor";
import useAssessmentViewerAccess from "@util/useAssessmentViewerAccess";

function simulateTakeAvailable() {
  const viteEnv = import.meta.env.VITE_ENV;
  const mode = import.meta.env.MODE;
  return (
    ["qa", "dev"].includes(viteEnv) ||
    ["qa", "dev", "development"].includes(mode)
  );
}

function personLabel(person, fallbackId) {
  if (!person) return fallbackId ? String(fallbackId) : "—";
  const name = `${person.first_name || ""} ${person.last_name || ""}`.trim();
  if (name && person.email) return `${name} (${person.email})`;
  return name || person.email || String(fallbackId || "—");
}

export default function AssessmentDetail() {
  const { t } = useTranslation();
  const { id, organizationId } = useParams();
  const [error, setError] = useState(null);
  const [assessment, setAssessment] = useState(null);
  const [simulating, setSimulating] = useState(false);
  const [filterConcept, setFilterConcept] = useState(null);

  useEffect(() => {
    fetchData();
  }, [id]);

  function fetchData() {
    axios
      .get(`/assessments/${id}/`)
      .then(({ data }) => {
        setAssessment(data.response[0] || null);
        setError(null);
      })
      .catch((err) => {
        setError(err);
        setAssessment(null);
      });
  }

  function simulateTake() {
    setSimulating(true);
    axios
      .post(`/assessments/${id}/simulate-take/`, { style: "random" })
      .then(() => {
        toast.success(t("simulate_take_success"));
        fetchData();
      })
      .catch((err) => toast.error(err))
      .finally(() => setSimulating(false));
  }

  function handleSelectConcept(concept) {
    setFilterConcept((current) => (current === concept ? null : concept));
  }

  const orgId = organizationId || assessment?.organization_id;
  const {
    canEvaluate,
    canViewResults,
    viewerLookupError,
    viewerLookupLoading,
  } = useAssessmentViewerAccess({
    assessment,
    organizationId: orgId,
  });

  if (error) return <ErrorView text={error} />;
  if (!assessment) return null;

  const activityStatus = Number(assessment.activity_status);
  const showSimulateTake =
    simulateTakeAvailable() && Number.isFinite(activityStatus) && activityStatus < 2;

  return (
    <div>
      <Group mb="lg">
        <PageTitle
          title={
            assessment.name ||
            assessment.assessment_instrument?.name ||
            String(assessment.id)
          }
          subtitle={t("assessment_singular")}
          icon={entityIcon.survey()}
          iconColor={entityColor.engagement}
        />
        <Badge variant="light">
          {assessment.activity_status_formatted || assessment.status_formatted}
        </Badge>
        {showSimulateTake && (
          <Button
            size="xs"
            variant="light"
            loading={simulating}
            onClick={simulateTake}
          >
            {t("simulate_take")}
          </Button>
        )}
      </Group>
      <Stack gap="md">
        <Card>
          <Stack gap="xs">
            <Text size="sm">
              {t("user_singular")}:{" "}
              {personLabel(assessment.user, assessment.user_id)}
            </Text>
            <Text size="sm">
              {t("assessment_instrument_singular")}:{" "}
              {assessment.assessment_instrument?.name ||
                assessment.assessment_instrument_id}
            </Text>
            <AssessmentActivityTimeline assessment={assessment} />
          </Stack>
        </Card>
        {Number(assessment.activity_status) >= 2 && canEvaluate && (
          <>
            <Card>
              <AssessmentStenResults
                assessment={assessment}
                onReload={fetchData}
                selectedConcept={filterConcept}
                onSelectConcept={handleSelectConcept}
              />
            </Card>
            {Number(assessment.activity_status) >= 3 && (
              <Card>
                <Accordion>
                  <Accordion.Item value="profiles">
                    <Accordion.Control>{t("cs:profile_plural")}</Accordion.Control>
                    <Accordion.Panel>
                      <AssessmentOpccResults
                        assessment={assessment}
                        organizationId={orgId}
                        hideHeading
                      />
                    </Accordion.Panel>
                  </Accordion.Item>
                </Accordion>
              </Card>
            )}
            <Card>
              <Accordion>
                <Accordion.Item value="responses">
                  <Accordion.Control>{t("assessment_responses")}</Accordion.Control>
                  <Accordion.Panel>
                    <AssessmentResponseLookup
                      assessment={assessment}
                      filterConcept={filterConcept}
                      onClearFilter={() => setFilterConcept(null)}
                      hideHeading
                    />
                  </Accordion.Panel>
                </Accordion.Item>
              </Accordion>
            </Card>
          </>
        )}
        {Number(assessment.activity_status) >= 2 && !canEvaluate && (
          <>
            {viewerLookupLoading && (
              <Card>
                <Group justify="center" gap="xs">
                  <Loader size="sm" />
                  <Text size="sm">{t("assessment_results_access_loading")}</Text>
                </Group>
              </Card>
            )}
            {!viewerLookupLoading && viewerLookupError && (
              <Card>
                <Text size="sm" c="dimmed">
                  {t("assessment_results_access_error")}
                </Text>
              </Card>
            )}
            {!viewerLookupLoading && !viewerLookupError && !canViewResults && (
              <Card>
                <Text size="sm" c="dimmed">
                  {t("assessment_results_unavailable")}
                </Text>
              </Card>
            )}
            {!viewerLookupLoading && !viewerLookupError && canViewResults && (
              <>
                <Card>
                  <AssessmentStenResults
                    assessment={assessment}
                    onReload={fetchData}
                    selectedConcept={filterConcept}
                    onSelectConcept={handleSelectConcept}
                  />
                </Card>
                {Number(assessment.activity_status) >= 3 && (
                  <Card>
                    <Accordion>
                      <Accordion.Item value="profiles">
                        <Accordion.Control>
                          {t("cs:profile_plural")}
                        </Accordion.Control>
                        <Accordion.Panel>
                          <AssessmentOpccResults
                            assessment={assessment}
                            organizationId={orgId}
                            hideHeading
                            readOnly
                          />
                        </Accordion.Panel>
                      </Accordion.Item>
                    </Accordion>
                  </Card>
                )}
                <Card>
                  <Accordion>
                    <Accordion.Item value="responses">
                      <Accordion.Control>
                        {t("assessment_responses")}
                      </Accordion.Control>
                      <Accordion.Panel>
                        <AssessmentResponseLookup
                          assessment={assessment}
                          filterConcept={filterConcept}
                          onClearFilter={() => setFilterConcept(null)}
                          hideHeading
                        />
                      </Accordion.Panel>
                    </Accordion.Item>
                  </Accordion>
                </Card>
              </>
            )}
          </>
        )}
        {orgId && (
          <Card>
            <AssessmentViewerLinksPanel
              organizationId={orgId}
              assessmentId={assessment.id}
              assigneeUserId={assessment.user_id}
              allowMutate
            />
          </Card>
        )}
      </Stack>
    </div>
  );
}
